import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectEmailAddress, selectLoginToken } from "../../features/states/slices";
import {
    CHECKOUT_BILLING_TERMS,
    CHECKOUT_PLAN_KEYS,
    fetchPaddleProductPrice,
    getCheckoutPlanDefinitions,
    getPurchasablePlanConfigs,
    initializePaddleCheckout,
    loadPaddleClient,
    mapMonthlyDisplayPrice,
    mapYearlyDisplayPrice,
    openPaddleCheckout,
} from "./checkoutPaddleServices";
import { fetchUserInfo } from "./accountDataServices";

function getCheckoutSuccessQueryFlag(queryParams) {
    const successValue = queryParams.get("success");
    if (successValue === null) {
        return false;
    }

    return successValue === "" || successValue === "true" || successValue === "1";
}

export default function useCheckoutPaddleModel() {
    const userInfo = useSelector(selectLoginToken);
    const emailAddress = useSelector(selectEmailAddress);
    const [queryParams] = useSearchParams();
    const [paddle, setPaddle] = useState();
    const [checkoutComplete, setCheckoutComplete] = useState(false);
    const [selectedPlanKey, setSelectedPlanKey] = useState(CHECKOUT_PLAN_KEYS.UNDER_100K);
    const [billingTermsByPlan, setBillingTermsByPlan] = useState({
        [CHECKOUT_PLAN_KEYS.UNDER_100K]: CHECKOUT_BILLING_TERMS.MONTHLY,
        [CHECKOUT_PLAN_KEYS.OVER_100K]: CHECKOUT_BILLING_TERMS.MONTHLY,
    });
    const [pricingByPlan, setPricingByPlan] = useState({});
    const [isPaddleLoading, setIsPaddleLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        async function setUpPaddle() {
            try {
                const paddleClient = await loadPaddleClient();
                if (!isMounted) {
                    return;
                }
                setPaddle(paddleClient);
                initializePaddleCheckout(paddleClient);
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                console.error("Failed to load Paddle client:", error);
            } finally {
                if (isMounted) {
                    setIsPaddleLoading(false);
                }
            }
        }

        setUpPaddle();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (checkoutComplete) {
            fetchUserInfo();
            return;
        }

        setCheckoutComplete(getCheckoutSuccessQueryFlag(queryParams));
    }, [checkoutComplete, queryParams]);

    const planDefinitions = useMemo(() => getCheckoutPlanDefinitions(), []);
    const purchasablePlans = useMemo(() => getPurchasablePlanConfigs(), []);
    const purchasablePlanKeys = useMemo(() => new Set(purchasablePlans.map((plan) => plan.key)), [purchasablePlans]);

    useEffect(() => {
        async function loadPrices() {
            if (!paddle || !purchasablePlans.length) {
                return;
            }

            const pricingEntries = await Promise.all(
                purchasablePlans
                    .filter((plan) => plan.monthly && plan.yearly)
                    .map(async (plan) => {
                        const [monthlyData, yearlyData] = await Promise.all([
                            fetchPaddleProductPrice(paddle, plan.monthly),
                            fetchPaddleProductPrice(paddle, plan.yearly),
                        ]);

                        return [
                            plan.key,
                            {
                                [CHECKOUT_BILLING_TERMS.MONTHLY]: mapMonthlyDisplayPrice(monthlyData, plan),
                                [CHECKOUT_BILLING_TERMS.ANNUAL]: mapYearlyDisplayPrice(yearlyData, plan),
                            },
                        ];
                    })
            );

            setPricingByPlan(Object.fromEntries(pricingEntries));
        }

        loadPrices();
    }, [paddle, purchasablePlans]);

    const plans = useMemo(() => {
        return planDefinitions.map((plan) => ({
            ...plan,
            billingTerm: billingTermsByPlan[plan.key] ?? CHECKOUT_BILLING_TERMS.MONTHLY,
            price: pricingByPlan[plan.key]?.[billingTermsByPlan[plan.key] ?? CHECKOUT_BILLING_TERMS.MONTHLY] ?? null,
            isPriceLoading: plan.isPurchasable
                && purchasablePlanKeys.has(plan.key)
                && (isPaddleLoading
                    || (!!paddle && !pricingByPlan[plan.key]?.[billingTermsByPlan[plan.key] ?? CHECKOUT_BILLING_TERMS.MONTHLY])),
        }));
    }, [billingTermsByPlan, isPaddleLoading, paddle, planDefinitions, pricingByPlan, purchasablePlanKeys]);

    const selectedPlan = useMemo(() => {
        return plans.find((plan) => plan.key === selectedPlanKey) ?? plans[0];
    }, [plans, selectedPlanKey]);

    const selectedPrice = selectedPlan?.isPurchasable ? selectedPlan.price ?? null : null;

    const hasPurchasablePlans = purchasablePlans.length > 0;
    const isPricingReady = hasPurchasablePlans && purchasablePlans.every((plan) => {
        return !!pricingByPlan[plan.key]?.[CHECKOUT_BILLING_TERMS.MONTHLY]
            && !!pricingByPlan[plan.key]?.[CHECKOUT_BILLING_TERMS.ANNUAL];
    });

    const showUpgradeSuccess = !!checkoutComplete;
    const showAlreadyPro = !checkoutComplete && (userInfo?.userTier === 1 || userInfo?.userTier === 2 || !!userInfo?.isPro);
    const showManagedPlan = !checkoutComplete && (userInfo?.userTier === 3 || userInfo?.userTier === 4);
    const showPricingSelection = !showUpgradeSuccess && !showAlreadyPro && !showManagedPlan;
    const canCheckout = !!selectedPlan?.isPurchasable && !!selectedPrice && !!paddle;

    const handlePlanChange = useCallback((planKey) => {
        setSelectedPlanKey(planKey);
    }, []);

    const handleBillingTermChange = useCallback((planKey, nextBillingTerm) => {
        setSelectedPlanKey(planKey);
        setBillingTermsByPlan((previousState) => ({
            ...previousState,
            [planKey]: nextBillingTerm,
        }));
    }, []);

    const handleCheckoutCompleted = useCallback((value) => {
        setCheckoutComplete(value);
    }, []);

    const launchCheckout = useCallback(() => {
        if (!selectedPlan?.isPurchasable || !selectedPrice) {
            return;
        }

        openPaddleCheckout({
            paddleClient: paddle,
            productID: selectedPrice.productID,
            tier: selectedPlan.tier,
            emailAddress,
            onSuccess: () => handleCheckoutCompleted(true),
        });
    }, [emailAddress, handleCheckoutCompleted, paddle, selectedPlan, selectedPrice]);

    return {
        plans,
        selectedPlan,
        selectedPrice,
        isPaddleLoading,
        isPricingReady,
        canCheckout,
        showUpgradeSuccess,
        showAlreadyPro,
        showManagedPlan,
        showPricingSelection,
        handlePlanChange,
        handleBillingTermChange,
        launchCheckout,
    };
}
